/**
 * Ledger — the ONE optimistic write ledger, generic over what it holds.
 *
 * Every optimistic write the SDK paints before the server answers — a row's
 * own fields (`RowWrites`), a sheet's metadata, a task, a scheduling link —
 * is an instance of this engine with its own `Ledger.Strategy`: how a change
 * lays over a value, which fields it writes, how a send body is cut from the
 * projection, and what one show announces. The settle rules below are the
 * engine's, so every instance settles alike.
 *
 * ## The projection
 *
 * A target's entry (`key`) is its `base` — the confirmed value of every
 * field a write touched (read from the target's display when a write first
 * touches the field, advanced by accepted writes and authoritative re-reads)
 * — and its writes in flight, in issue order. The target SHOWS
 *
 *     projection = base ⊕ write₁ ⊕ … ⊕ writeₙ          (issue order)
 *
 * A write is a change, not a snapshot (`Strategy.merge` lays it over the
 * projection), so two writes of one field never carry each other's stale
 * copy, and a refusal removes exactly its own change.
 *
 * ## One settle rule (field-set scoped)
 *
 * The server applies a target's writes in arrival order (= send order) and
 * replaces each field a body names. Every body is therefore built at SEND
 * time from the projection, restricted to the write's own fields
 * (`Strategy.body`), so a body carries every older write of those fields
 * still in flight:
 *
 * - accepted: the write's body is what the server holds for its fields. It
 *   folds into `base` once every older write settled (an older write still
 *   in flight keeps a later-landed one out of `base`).
 * - refused: the write leaves the ledger and its fields go back to the
 *   projection of the writes that remain — never to a value captured when it
 *   was issued, so a refused older write never erases a newer one that
 *   landed. When a LATER send carried its fields, the surviving projection
 *   of those fields is sent again (the take-back), so the server does not
 *   keep what the SDK took back — while the owner is live
 *   (`Options.live`); a take-back the owner's end abandons is reported
 *   (`Options.abandoned`) and not sent.
 * - an authoritative re-read replaces `base` (`beginRead` / `endRead`). The
 *   read stamps the send count when it opens: a write SENT BEFORE it reached
 *   the server before the read did, so the read's answer already holds it
 *   and it is never laid over that answer again; a write sent after it (or
 *   never sent) that is accepted while the read is open lays over the
 *   answer, in issue order. A field a sent body still unanswered carries
 *   keeps the base that body was built over (accepted, the body replaces
 *   what the read saw; refused, the read's value becomes the base).
 *
 * ## One show, one emit
 *
 * The ledger writes every display copy from the one projection (`Display`),
 * silently, then emits the ONE event list `Strategy.events` makes of the
 * change, to each of the target's sinks once.
 *
 * ## The target's lifecycle (`forget`)
 *
 * A structural edge of the target's life — a row's landed delete — ends its
 * entry: `forget` abandons every write in flight and every read open, and
 * shows nothing. An abandoned write's late answer touches nothing — no fold,
 * no take-back paint, no take-back send — and one not yet sent still goes
 * out with the body it had when abandoned. `Forgotten.landed` tells the
 * owner whether the server applied an abandoned body after the edge: the
 * owner's own authoritative read (a restore's re-read) then owes another.
 *
 * ## Instances, and the mechanisms that are not (yet)
 *
 * Instances: `RowWrites` (a row's own fields), `CellWrites` (a sheet's
 * cells: its date-keyed and timepoint lanes, the durable writes waiting on a
 * debounced persist, the event edits settled by event identity), the
 * sheet-metadata ledger (`SheetsClient`), the task ledger (`TasksClient`),
 * the scheduling-link ledger (`SchedulingLinksClient`, each update sent in
 * two parts). Still separate: the event overlays
 * (`EventOverlayProjector`, the events client's
 * pending map, `EventsFacade`'s overlay), the move ledgers (`RowAccessor`'s
 * pending moves, `PendingCellMoves`, `SourceRow`'s pending moves) and the
 * promotion-time split (`PendingPersist` / `PendingRowPatchWrite`).
 *
 * `MutationController` (`../mutation/mutation-controller.ts`) is a
 * different layer: it runs one optimistic op's lifecycle (append → submit
 * → confirm / drop) over an `Overlay` of whole items, with no field sets,
 * no send-time bodies and no reads; an instance of this engine could be its
 * overlay (an op is a change, confirm is `accept`, drop is `refuse`). The
 * write-ahead log's `RollbackRecord` (`../tx/tx-log.ts`) is not a ledger: it
 * journals that a gesture's frame was unwound (how many of its steps had
 * landed), after the ledgers took each write back — a record of a rollback,
 * never a pending state that shows.
 */

import type { Option } from "../core/option.ts";
import type { Actor, Invalidation, InvalidationSink } from "../tx/invalidation.ts";
import { Tracing } from "@tento-core/tracing";

/** One write's bookkeeping. */
type WriteState<T extends Ledger.Types> = {
  readonly write: Ledger.Write<T>;
  readonly change: T["change"];
  readonly fields: ReadonlySet<T["field"]>;
  /** Issue number: a later-issued write has a larger one. */
  readonly issued: number;
  /** The target's send counter when the write was issued: a send numbered
   *  after it carried this write's change (in the fields they share). */
  readonly issuedAtSend: number;
  /** Who a take-back of this write is announced as. */
  readonly correction: Actor;
  /** The send number of this write's own body — its first part's (`sendPart`) — (0: never sent). */
  ownSend: number;
  /** Whether it was sent in parts (`sendPart`): each part is its own send. */
  parted: boolean;
  /** The send numbers of its parts (`sendPart`). */
  readonly parts: number[];
  /** The transport this write was sent through (a take-back of its fields goes the same way). */
  transport: Option<Ledger.Transport<T>>;
  acked: boolean;
  settled: boolean;
  /** Event names whose announcement the writer deferred (`Begin.quiet`); `announce` emits them. */
  deferred: readonly string[];
  /** A take-back write (`#takeBack`), not a writer's. */
  readonly takeBack: boolean;
  /** The body this write was last sent with (parts laid together; `null`: never sent). */
  sent: Option<T["fields"]>;
  /** Abandoned by `forget` and not yet answered (`null`: tracked, or answered). */
  abandoned: Option<Abandonment<T>>;
};

/** What `forget` abandoned of one target: the projection its unsent writes still send from, and the answers it waits for. */
type Abandonment<T extends Ledger.Types> = {
  readonly projection: T["fields"];
  /** A late-accepted write's body: the one it was sent with, else (its caller sent it itself) its change over the base. */
  readonly bodyOf: (state: WriteState<T>) => T["fields"];
  pending: number;
  /** The bodies accepted after the edge, with their issue numbers. */
  readonly landed: { readonly issued: number; readonly body: T["fields"] }[];
  readonly done: (landed: readonly T["fields"][]) => void;
};

/** One send of a target: its number, the fields its body carried and the transport it went through. */
type SendRecord<T extends Ledger.Types> = {
  readonly n: number;
  readonly fields: ReadonlySet<T["field"]>;
  readonly transport: Ledger.Transport<T>;
  /** Whether its answer arrived (accepted or refused). */
  answered: boolean;
  /** A part (`sendPart`) whose body landed — accepted, or failed in a way
   *  that may have stored it: a refusal of its write takes it back. */
  landed: boolean;
};

/** One read of a target in flight (`beginRead`). */
type ReadState<T extends Ledger.Types> = {
  readonly read: Ledger.Read<T>;
  /** The writes accepted while the read was open that its answer may predate (sent after it opened, or never sent). */
  readonly landed: WriteState<T>[];
  /** The target's send counter when the read opened: a send numbered at or below it reached the server first. */
  readonly sentAtBegin: number;
};

/** One target's entry: its confirmed base, writes in flight, sends and reads. */
type Entry<T extends Ledger.Types> = {
  base: T["fields"];
  readonly writes: WriteState<T>[];
  readonly sends: SendRecord<T>[];
  sent: number;
  readonly reads: Set<ReadState<T>>;
  /** A read's answer for a field a sent, unanswered body carries: the body
   *  replaces it on the server if accepted, so the base keeps the value the
   *  body was built over until then; a refusal of the last such send makes
   *  the read's value the base. */
  readonly held: Map<T["field"], T["fields"]>;
};

export class Ledger<T extends Ledger.Types> {
  readonly #targets = new Map<T["key"], Entry<T>>();
  readonly #states = new WeakMap<Ledger.Write<T>, WriteState<T>>();
  readonly #readStates = new WeakMap<Ledger.Read<T>, ReadState<T>>();
  readonly #displays: Ledger.Display<T>[] = [];
  readonly #strategy: Ledger.Strategy<T>;
  readonly #options: Ledger.Options<T>;
  #issued = 0;

  constructor(strategy: Ledger.Strategy<T>, options: Ledger.Options<T>) {
    this.#strategy = strategy;
    this.#options = options;
  }

  /**
   * Register a display copy. `first`: its values are the ones a write's base
   * reads first. Returns the unregistration.
   */
  addDisplay(display: Ledger.Display<T>, rank: "first" | "last"): () => void {
    if (rank === "first") this.#displays.unshift(display);
    else this.#displays.push(display);
    return () => {
      const at = this.#displays.indexOf(display);
      if (at >= 0) this.#displays.splice(at, 1);
    };
  }

  /**
   * Enter `change` as a write of `key` and SHOW it — synchronously, before
   * this returns: every display copy takes the new projection and the
   * strategy's events are announced as `actor` (its deferred names kept for
   * `announce` when `quiet`). Send it with `send` (or `run`) and settle it
   * with `accept` / `refuse`; until then it stays in the projection. A
   * display that throws takes the write back (nothing was sent) and the
   * throw propagates.
   */
  begin(key: T["key"], change: T["change"], actor: Actor, opts: Ledger.Begin<T>): Ledger.Write<T> {
    const fields = this.#strategy.fieldsOf(change);
    const entry = this.#entry(key);
    // A field no write in flight touches is confirmed as the display holds
    // it NOW (a re-read or sync that landed since the first write shows).
    const touched = new Set<T["field"]>();
    for (const w of entry.writes) for (const f of w.fields) touched.add(f);
    const view = this.#view(key, opts.capture);
    const untouched = [...fields].filter((f) => !touched.has(f));
    if (untouched.length > 0) entry.base = this.#strategy.adopt(entry.base, view, untouched);
    const before = this.#project(entry);
    const write: Ledger.Write<T> = Object.freeze({ key, fields: new Set(fields) });
    const state: WriteState<T> = {
      write,
      change,
      fields,
      issued: ++this.#issued,
      issuedAtSend: entry.sent,
      correction: opts.correction,
      ownSend: 0,
      parted: false,
      parts: [],
      transport: null,
      acked: false,
      settled: false,
      deferred: [],
      takeBack: false,
      sent: null,
      abandoned: null,
    };
    this.#states.set(write, state);
    entry.writes.push(state);
    const after = this.#project(entry);
    let deferred: Option<readonly string[]>;
    try {
      deferred = this.#show(key, before, after, actor, opts.quiet);
    } catch (err) {
      // Notification can issue a newer write before another observer throws.
      // Refuse against the surviving projection, including any correction
      // owed by a newer send that carried this failed write's fields.
      try {
        this.#refuse(write, err, "");
      } catch (_) {
        // A take-back observer also failed; the initiating error stays visible.
      }
      throw err;
    }
    state.deferred = opts.quiet ? deferred ?? [] : [];
    return write;
  }

  /** Whether a write of `field` of `key` is in flight. */
  inFlight(key: T["key"], field: T["field"]): boolean {
    return this.#targets.get(key)?.writes.some((w) => w.fields.has(field)) ?? false;
  }

  /** Whether a writer's write of `key` is in flight (a take-back of the ledger's own does not count). */
  writing(key: T["key"]): boolean {
    return this.#targets.get(key)?.writes.some((w) => !w.takeBack) ?? false;
  }

  /** The target's projection while it has writes in flight or reads open; `null` when idle. */
  project(key: T["key"]): Option<T["fields"]> {
    const entry = this.#targets.get(key);
    return entry == null ? null : this.#project(entry);
  }

  /** The target's confirmed base while it has writes in flight or reads open; `null` when idle. */
  confirmed(key: T["key"]): Option<T["fields"]> {
    return this.#targets.get(key)?.base ?? null;
  }

  /**
   * Send `write` through `transport`: the body is the projection of the
   * write's fields NOW (`Strategy.body`), so it carries every older write of
   * those fields still in flight. Resolves with the transport's answer;
   * settle the write on it (`accept` / `refuse`).
   */
  send(write: Ledger.Write<T>, transport: Ledger.Transport<T>): Promise<T["answer"]> {
    const state = this.#stateOf(write);
    const gone = state.abandoned;
    if (gone != null) {
      state.sent = this.#strategy.body(gone.projection, state.fields);
      return transport(state.sent);
    }
    const entry = this.#entry(write.key);
    const body = this.#strategy.body(this.#project(entry), state.fields);
    state.sent = body;
    const n = ++entry.sent;
    entry.sends.push({ n, fields: state.fields, transport, answered: false, landed: false });
    state.ownSend = n;
    state.transport = transport;
    return transport(body);
  }

  /**
   * Send `writes` (of one target) as ONE body through `transport`: the body
   * is the projection of all their fields now, and every one of them counts
   * it as its own send. Settle each on the answer. `null` when `writes` is
   * empty (nothing sent).
   */
  sendAll(writes: readonly Ledger.Write<T>[], transport: Ledger.Transport<T>): Promise<Option<T["answer"]>> {
    const first = writes[0];
    if (first == null) return Promise.resolve(null);
    const states = writes.map((w) => this.#stateOf(w));
    const fields = new Set<T["field"]>();
    for (const state of states) for (const f of state.fields) fields.add(f);
    const gone = states.find((state) => state.abandoned != null)?.abandoned ?? null;
    const entry = gone == null ? this.#entry(first.key) : null;
    const body = this.#strategy.body(entry == null ? nonnullOf(gone).projection : this.#project(entry), fields);
    for (const state of states) state.sent = body;
    if (entry == null) return transport(body);
    const n = ++entry.sent;
    entry.sends.push({ n, fields, transport, answered: false, landed: false });
    for (const state of states) {
      state.ownSend = n;
      state.transport = transport;
    }
    return transport(body);
  }

  /**
   * Send the part `only` of `write` (its fields among them) through
   * `transport` — a write whose fields live in several places the server
   * answers for separately (a link row and its public projection row), each
   * part a body and a send of its own: the body is the projection of those
   * fields now. A part whose answer lands — accepted, or failed with
   * `mayHaveLanded(err)` — counts as carrying the write's fields: a refusal
   * of the write takes it back like a later send that carried them. Settle
   * the write (`accept` / `refuse` / `settle`) once its parts answered. A
   * write's first part is its own send for a read (`beginRead`).
   */
  sendPart(
    write: Ledger.Write<T>,
    only: ReadonlySet<T["field"]>,
    transport: Ledger.Transport<T>,
    mayHaveLanded: (err: unknown) => boolean,
  ): Promise<T["answer"]> {
    const state = this.#stateOf(write);
    const fields = new Set<T["field"]>();
    for (const f of only) if (state.fields.has(f)) fields.add(f);
    const gone = state.abandoned;
    const entry = gone == null ? this.#entry(write.key) : null;
    const body = this.#strategy.body(entry == null ? nonnullOf(gone).projection : this.#project(entry), fields);
    state.sent = state.sent == null ? body : this.#strategy.overlay(state.sent, body);
    if (entry == null) return transport(body);
    const n = ++entry.sent;
    const record: SendRecord<T> = { n, fields, transport, answered: false, landed: false };
    entry.sends.push(record);
    if (state.ownSend === 0) state.ownSend = n;
    state.parted = true;
    state.parts.push(n);
    state.transport ??= transport;
    return transport(body).then(
      (answer) => {
        record.landed = true;
        return answer;
      },
      (err: unknown) => {
        if (mayHaveLanded(err)) record.landed = true;
        throw err;
      },
    );
  }

  /**
   * `send`, then settle on the answer: accepted resolves with it; a refusal
   * or no answer takes the write back (`refuse`) and rejects with the
   * strategy's wrap of it, `context` leading its details.
   */
  async run(write: Ledger.Write<T>, transport: Ledger.Transport<T>, context: string): Promise<T["answer"]> {
    let answer: T["answer"];
    try {
      answer = await this.send(write, transport);
    } catch (err) {
      throw this.refuse(write, err, context);
    }
    this.accept(write);
    return answer;
  }

  /**
   * The server accepted `write`: every leading accepted write folds into the
   * target's base (issue order). Nothing is shown: the projection is
   * unchanged. Settling twice changes nothing.
   */
  accept(write: Ledger.Write<T>): void {
    const state = this.#states.get(write);
    if (state == null) return;
    if (state.abandoned != null) {
      lateAnswer(state, true);
      return;
    }
    if (state.settled) return;
    state.settled = true;
    state.acked = true;
    const entry = this.#targets.get(write.key);
    if (entry == null) return;
    this.#answered(entry, state, true);
    // A write sent before a read opened reached the server before the read
    // did: the read's answer holds it already.
    for (const read of entry.reads) {
      if (state.ownSend === 0 || state.ownSend > read.sentAtBegin) read.landed.push(state);
    }
    this.#fold(entry);
    this.#forgetIfIdle(write.key, entry);
  }

  /**
   * The server refused `write` (or did not answer): it leaves the ledger,
   * its fields go back to the projection of the writes that remain (shown
   * and announced as the write's correction actor), and when a later send
   * carried its fields the surviving projection of them is sent again (while
   * the owner is live). Returns the strategy's wrap of `err`. Settling twice
   * returns the same wrap and changes nothing.
   */
  refuse(write: Ledger.Write<T>, err: unknown, context: string): T["error"] {
    return this.#refuse(write, err, context).error;
  }

  /** `refuse`, answering the take-back's outcome too (`Settled.takenBack`). */
  #refuse(
    write: Ledger.Write<T>,
    err: unknown,
    context: string,
  ): { readonly error: T["error"]; readonly takenBack: Promise<Option<T["error"]>> } {
    const error = this.#strategy.wrap(err, context);
    const none = { error, takenBack: Promise.resolve(null) };
    const state = this.#states.get(write);
    if (state == null) return none;
    if (state.abandoned != null) {
      lateAnswer(state, false);
      return none;
    }
    if (state.settled) return none;
    state.settled = true;
    const entry = this.#targets.get(write.key);
    if (entry == null) return none;
    const at = entry.writes.indexOf(state);
    if (at < 0) return none;
    const before = this.#project(entry);
    entry.writes.splice(at, 1);
    this.#answered(entry, state, false);
    // Where each field it wrote was carried since it was issued: a later
    // send, or a part of its own that landed. A write sent whole takes back
    // through its own transport (else the first carrier's); a write sent in
    // parts takes back per transport of each field's latest carrier.
    const carried = new Set<T["field"]>();
    let via: Option<Ledger.Transport<T>> = state.transport;
    const byField = new Map<T["field"], Ledger.Transport<T>>();
    for (const send of entry.sends) {
      const own = state.parted ? state.parts.includes(send.n) : send.n === state.ownSend;
      if (own ? !send.landed : send.n <= state.issuedAtSend) continue;
      let carries = false;
      for (const f of send.fields) {
        if (!state.fields.has(f)) continue;
        carried.add(f);
        byField.set(f, send.transport);
        carries = true;
      }
      if (carries && via == null) via = send.transport;
    }
    this.#fold(entry);
    let showFailure: Option<() => never> = null;
    try {
      this.#show(write.key, before, this.#project(entry), state.correction, false);
    } catch (err) {
      showFailure = () => { throw err; };
    }
    // A host observer's failure must not leave a refused field in a later
    // accepted body: schedule every owed correction before propagating it.
    const takeBacks: Promise<Option<T["error"]>>[] = [];
    if (state.parted) {
      const groups = new Map<Ledger.Transport<T>, Set<T["field"]>>();
      for (const [f, transport] of byField) {
        const group = groups.get(transport) ?? new Set<T["field"]>();
        group.add(f);
        groups.set(transport, group);
      }
      for (const [transport, fields] of groups) {
        takeBacks.push(this.#takeBack(write.key, fields, state.correction, transport, error));
      }
    } else if (carried.size > 0 && via != null) {
      takeBacks.push(this.#takeBack(write.key, carried, state.correction, via, error));
    }
    this.#forgetIfIdle(write.key, entry);
    if (showFailure != null) showFailure();
    return {
      error,
      takenBack: Promise.all(takeBacks).then((outcomes) => outcomes.find((e) => e != null) ?? null),
    };
  }

  /**
   * `accept` or `refuse` `write` (`verdict`), answering the target as that
   * settle left it — its projection and confirmed base, also when the
   * settle left it idle (`null` when the ledger held no entry for it) — and,
   * for a refusal, the strategy's wrap of the error. The settle a pure
   * model's caller shows itself (a refusal: its fields back to
   * `projection`).
   */
  settle(write: Ledger.Write<T>, verdict: Ledger.Verdict): Ledger.Settled<T> {
    const entry = this.#targets.get(write.key) ?? null;
    let error: Option<T["error"]> = null;
    let takenBack: Promise<Option<T["error"]>> = Promise.resolve(null);
    if (verdict.accepted) {
      this.accept(write);
    } else {
      const refused = this.#refuse(write, verdict.error, verdict.context);
      error = refused.error;
      takenBack = refused.takenBack;
    }
    if (entry == null) return { error, projection: null, confirmed: null, takenBack };
    return { error, projection: this.#project(entry), confirmed: entry.base, takenBack };
  }

  /**
   * Lay `fields` over the target's confirmed base — an authoritative answer
   * about those fields alone. The writes in flight keep replaying over it;
   * nothing is shown.
   */
  confirm(key: T["key"], fields: T["fields"]): void {
    const entry = this.#targets.get(key);
    if (entry == null) return;
    entry.base = this.#strategy.overlay(entry.base, fields);
  }

  /**
   * End `key`'s entry — a structural edge of the target's life (a row's
   * landed delete): every write of it in flight is ABANDONED and every read
   * open on it dropped; nothing is shown or announced. An abandoned write's
   * late answer touches nothing (`accept` / `refuse` / `settle` of it change
   * nothing and send no take-back; `refuse` still answers the strategy's
   * wrap), and `send` of one not yet sent sends the body it had when
   * abandoned (the projection then). A later `begin` of `key` opens a fresh
   * entry. Answers what was abandoned (`Ledger.Forgotten`): an accepted
   * late answer reports its body there, for the owner to install.
   */
  forget(key: T["key"]): Ledger.Forgotten<T> {
    const entry = this.#targets.get(key);
    if (entry == null) return { confirmed: null, abandoned: 0, landed: Promise.resolve([]) };
    this.#targets.delete(key);
    for (const read of entry.reads) this.#readStates.delete(read.read);
    entry.reads.clear();
    let done: (landed: readonly T["fields"][]) => void = () => {};
    const landed = new Promise<readonly T["fields"][]>((resolve) => {
      done = resolve;
    });
    const base = entry.base;
    const strategy = this.#strategy;
    const gone: Abandonment<T> = {
      projection: this.#project(entry),
      bodyOf: (state) => state.sent ?? strategy.body(strategy.merge(base, state.change), state.fields),
      pending: 0,
      landed: [],
      done,
    };
    for (const state of entry.writes) {
      // An accepted write an older one kept out of the base was answered
      // before the edge: nothing of it is late.
      if (state.settled) continue;
      state.settled = true;
      state.abandoned = gone;
      gone.pending++;
    }
    if (gone.pending === 0) done([]);
    return { confirmed: entry.base, abandoned: gone.pending, landed };
  }

  /**
   * The deferred announcements of `write` (`Begin.quiet`), as `actor`.
   * Emitted once; a second call emits nothing.
   */
  announce(write: Ledger.Write<T>, actor: Actor): void {
    const state = this.#states.get(write);
    if (state == null) return;
    const names = state.deferred;
    state.deferred = [];
    const sinks = this.#options.sinks(write.key);
    for (const name of names) {
      const event = this.#strategy.deferred(write.key, name, actor);
      for (const sink of sinks) sink.emit(event);
    }
  }

  /** Open an authoritative read of `key` — before its request is sent; close it with `endRead` / `settleRead`. */
  beginRead(key: T["key"]): Ledger.Read<T> {
    const entry = this.#entry(key);
    const read: Ledger.Read<T> = Object.freeze({ key });
    const state: ReadState<T> = { read, landed: [], sentAtBegin: entry.sent };
    this.#readStates.set(read, state);
    entry.reads.add(state);
    return read;
  }

  /**
   * Close `read` and SHOW the result. `confirmed` is the target as the
   * server answered (every field the read returns), `null` when the read
   * failed or was dropped (nothing changes). The writes accepted during the
   * read that its answer may predate lay over it in issue order, that
   * becomes the target's base, and the new projection is shown and
   * announced as `actor`. Answers whether anything was shown.
   */
  endRead(read: Ledger.Read<T>, confirmed: Option<T["fields"]>, actor: Actor): boolean {
    const entry = this.#targets.get(read.key);
    if (entry == null || !this.#readStates.has(read)) return false;
    const before = this.#strategy.overlay(this.#view(read.key, null), this.#project(entry));
    const installed = this.#install(read, confirmed);
    const shown = installed != null && this.#show(read.key, before, this.#project(entry), actor, false) != null;
    this.#forgetIfIdle(read.key, entry);
    return shown;
  }

  /**
   * Close `read` as `endRead` does, but show NOTHING: the caller shows the
   * returned projection (the confirmed base with the writes in flight
   * replayed). `null` when `confirmed` is `null` (nothing changes).
   */
  settleRead(read: Ledger.Read<T>, confirmed: Option<T["fields"]>): Option<T["fields"]> {
    const entry = this.#targets.get(read.key);
    if (entry == null) return null;
    const installed = this.#install(read, confirmed);
    const view = installed == null ? null : this.#project(entry);
    this.#forgetIfIdle(read.key, entry);
    return view;
  }

  /**
   * A multi-target write group (a rename: the label write and the formula
   * rewrites it causes): writes added to it are taken back together by
   * `refuseAll`.
   */
  group(): Ledger.Group<T> {
    const members: Ledger.Write<T>[] = [];
    return {
      add: (write) => {
        members.push(write);
      },
      members: () => [...members],
      refuseAll: (err, context) => {
        for (const member of members) this.refuse(member, err, context);
      },
    };
  }

  // ── internals ─────────────────────────────────────────────────────

  #stateOf(write: Ledger.Write<T>): WriteState<T> {
    const state = this.#states.get(write);
    if (state == null) throw new Error(`${this.#options.name}: a write this ledger did not enter`);
    return state;
  }

  #entry(key: T["key"]): Entry<T> {
    let entry = this.#targets.get(key);
    if (entry == null) {
      entry = { base: this.#strategy.empty(), writes: [], sends: [], sent: 0, reads: new Set(), held: new Map() };
      this.#targets.set(key, entry);
    }
    return entry;
  }

  /**
   * Close `read`'s bookkeeping and install `confirmed` as the base (see
   * `endRead`): the base installed, `null` when nothing changed. The entry
   * is left for the caller to forget.
   */
  #install(read: Ledger.Read<T>, confirmed: Option<T["fields"]>): Option<T["fields"]> {
    const state = this.#readStates.get(read);
    const entry = this.#targets.get(read.key);
    if (state == null || entry == null) return null;
    this.#readStates.delete(read);
    entry.reads.delete(state);
    if (confirmed == null) return null;
    let base: T["fields"] = this.#strategy.overlay(this.#strategy.empty(), confirmed);
    for (const w of [...state.landed].sort((a, b) => a.issued - b.issued)) base = this.#strategy.merge(base, w.change);
    // A field a sent body still unanswered carries keeps the base that body
    // was built over (accepted, the body replaces what this read saw); the
    // read's value waits for that send's refusal.
    const open = [...openFields(entry)];
    for (const f of open) entry.held.set(f, base);
    entry.base = open.length === 0 ? base : this.#strategy.adopt(base, entry.base, open);
    return entry.base;
  }

  /** The target as its displays hold it now: the first display holding a copy, else `capture`. */
  #view(key: T["key"], capture: Option<() => T["fields"]>): T["fields"] {
    for (const display of this.#displays) {
      const held = display.peek(key);
      if (held != null) return held;
    }
    return capture?.() ?? this.#strategy.empty();
  }

  #project(entry: Entry<T>): T["fields"] {
    let view = entry.base;
    for (const w of entry.writes) view = this.#strategy.merge(view, w.change);
    return view;
  }

  /** Fold every leading accepted write into the base. */
  #fold(entry: Entry<T>): void {
    while (entry.writes[0]?.acked === true) {
      const head = entry.writes.shift();
      if (head != null) entry.base = this.#strategy.merge(entry.base, head.change);
    }
  }

  /**
   * `state`'s send was answered. A read value held for a field it carried
   * (`Entry.held`): accepted, the body replaced it on the server, so it is
   * dropped; refused, once no other unanswered send carries the field, it
   * becomes the base.
   */
  #answered(entry: Entry<T>, state: WriteState<T>, accepted: boolean): void {
    for (const n of state.parted ? state.parts : [state.ownSend]) {
      const record = entry.sends.find((s) => s.n === n);
      if (record == null || record.answered) continue;
      record.answered = true;
      if (entry.held.size === 0) continue;
      const open = openFields(entry);
      for (const f of record.fields) {
        const read = entry.held.get(f);
        if (read == null) continue;
        if (accepted || record.landed) {
          entry.held.delete(f);
        } else if (!open.has(f)) {
          entry.base = this.#strategy.adopt(entry.base, read, [f]);
          entry.held.delete(f);
        }
      }
    }
  }

  #forgetIfIdle(key: T["key"], entry: Entry<T>): void {
    if (entry.writes.length === 0 && entry.reads.size === 0 && this.#targets.get(key) === entry) this.#targets.delete(key);
  }

  /**
   * Send the surviving projection of `fields` (the refused write's fields a
   * later send carried): a write of its own that changes nothing
   * (`Strategy.takeBack`) — its body is built at send time from the
   * projection like every other, so a later refusal of a write it carried
   * takes back through the same rule. Its failure is traced: the refused
   * write already rejected. Once the owner is no longer live nothing is
   * sent: the abandoned correction is reported (`Options.abandoned`).
   */
  #takeBack(
    key: T["key"],
    fields: ReadonlySet<T["field"]>,
    correction: Actor,
    transport: Ledger.Transport<T>,
    refusal: T["error"],
  ): Promise<Option<T["error"]>> {
    const entry = this.#targets.get(key);
    if (entry == null) return Promise.resolve(null);
    if (!this.#options.live()) {
      this.#options.abandoned(key, fields, refusal);
      return Promise.resolve(refusal);
    }
    const write: Ledger.Write<T> = Object.freeze({ key, fields: new Set(fields) });
    const state: WriteState<T> = {
      write,
      change: this.#strategy.takeBack(fields),
      fields,
      issued: ++this.#issued,
      issuedAtSend: entry.sent,
      correction,
      ownSend: 0,
      parted: false,
      parts: [],
      transport: null,
      acked: false,
      settled: false,
      deferred: [],
      takeBack: true,
      sent: null,
      abandoned: null,
    };
    this.#states.set(write, state);
    entry.writes.push(state);
    return this.run(write, transport, this.#options.takeBackContext).then(
      () => null,
      (err: unknown) => {
        Tracing.warn("net")(`${this.#options.name}/take-back-refused`, {
          ...this.#options.describe(key),
          fields: [...fields].join(","),
          error: err instanceof Error ? err.message : String(err),
        });
        return this.#strategy.wrap(err, this.#options.takeBackContext);
      },
    );
  }

  /**
   * Write `after` into every display (only what changed from `before`) and
   * announce the strategy's events as `actor`. Answers the deferred event
   * names (`quiet`), or `null` when nothing changed (nothing shown).
   */
  #show(
    key: T["key"],
    before: T["fields"],
    after: T["fields"],
    actor: Actor,
    quiet: boolean,
  ): Option<readonly string[]> {
    const delta = this.#strategy.delta(before, after);
    if (delta == null) return null;
    const extra: string[] = [];
    for (const display of [...this.#displays]) {
      for (const name of display.show(key, delta)) if (!extra.includes(name)) extra.push(name);
    }
    const events = this.#strategy.events(key, { before, after, delta, extra }, actor, quiet);
    for (const sink of this.#options.sinks(key)) for (const event of events.now) sink.emit(event);
    return events.deferred;
  }
}

/** Types that go with `Ledger`. */
export namespace Ledger {
  /** The type bundle of one instance. */
  export interface Types {
    /** The target's identity (a `Map` key). */
    readonly key: unknown;
    /** One write's change (a patch, a merge, a move). */
    readonly change: unknown;
    /** The confirmed / projected value, and a send body (partial: a field left out is not part of it). */
    readonly fields: object;
    /** A field name of `fields`. */
    readonly field: string;
    /** What one show changes (`Strategy.delta`). */
    readonly delta: unknown;
    /** A transport's answer. */
    readonly answer: unknown;
    /** What a refusal rejects with (`Strategy.wrap`). */
    readonly error: Error;
  }

  /** How one instance's values behave. */
  export interface Strategy<T extends Types> {
    /** The value with no field (a new entry's base). */
    empty(): T["fields"];
    /** `change` laid over `fields`. */
    merge(fields: T["fields"], change: T["change"]): T["fields"];
    /** The fields `change` writes (what a refusal takes back, what a body carries). */
    fieldsOf(change: T["change"]): ReadonlySet<T["field"]>;
    /** `fields` restricted to `only` — a send body (a field `fields` lacks: its cleared value). */
    body(fields: T["fields"], only: ReadonlySet<T["field"]>): T["fields"];
    /** `fields` with each of `only` taken from `from` (cleared when `from` lacks it). */
    adopt(fields: T["fields"], from: T["fields"], only: Iterable<T["field"]>): T["fields"];
    /** `over` laid on `under`: every field `over` holds replaces. */
    overlay(under: T["fields"], over: T["fields"]): T["fields"];
    /** The change of a take-back write of `fields` (it changes nothing: its body is the projection). */
    takeBack(fields: ReadonlySet<T["field"]>): T["change"];
    /** What changed from `before` to `after`; `null`: nothing (nothing is shown or announced). */
    delta(before: T["fields"], after: T["fields"]): Option<T["delta"]>;
    /** The events of one show, and the names deferred when `quiet`. */
    events(key: T["key"], show: Show<T>, actor: Actor, quiet: boolean): Events;
    /** The event of one deferred name (`Ledger.announce`). */
    deferred(key: T["key"], name: string, actor: Actor): Invalidation;
    /** The error a refusal of `err` rejects with (`err` itself when it already is one). */
    wrap(err: unknown, context: string): T["error"];
  }

  /** One show: the values around it, what changed, and the extra names the displays reported. */
  export type Show<T extends Types> = {
    readonly before: T["fields"];
    readonly after: T["fields"];
    readonly delta: T["delta"];
    readonly extra: readonly string[];
  };

  /** `now`: emitted at once; `deferred`: names a quiet begin keeps for `announce`. */
  export type Events = { readonly now: readonly Invalidation[]; readonly deferred: readonly string[] };

  /** A strategy's `events` answer for a pure model: nothing announced. */
  export const SILENT: Events = Object.freeze({ now: Object.freeze([]), deferred: Object.freeze([]) });

  /**
   * One display copy. `peek` answers the fields it holds for a target
   * (`null`: it holds no copy); `show` applies `delta` SILENTLY (the ledger
   * announces) and answers any extra event names the change produced there.
   */
  export interface Display<T extends Types> {
    peek(key: T["key"]): Option<T["fields"]>;
    show(key: T["key"], delta: T["delta"]): readonly string[];
  }

  /** How an instance is owned and where it announces. */
  export type Options<T extends Types> = {
    /** Where `key`'s events go, each sink once. */
    readonly sinks: (key: T["key"]) => readonly InvalidationSink[];
    /** The instance's name: its trace (`<name>/take-back-refused`) and its errors. */
    readonly name: string;
    /** The details a take-back's own refusal is wrapped with. */
    readonly takeBackContext: string;
    /** A target as its traces name it (the trace record's own keys). */
    readonly describe: (key: T["key"]) => { readonly [key: string]: string };
    /** Whether the owner may still send: a take-back due once it answers `false` is not sent. */
    readonly live: () => boolean;
    /** A take-back the owner's end abandoned: `error` is the refusal it would have corrected. */
    readonly abandoned: (key: T["key"], fields: ReadonlySet<T["field"]>, error: T["error"]) => void;
  };

  /** How `begin` enters a write. */
  export type Begin<T extends Types> = {
    /** Who a take-back of the write is announced as. */
    readonly correction: Actor;
    /** Defer the strategy's deferred names (`announce` emits them). */
    readonly quiet: boolean;
    /** The target's fields when no display holds a copy (`null`: none). */
    readonly capture: Option<() => T["fields"]>;
  };

  /** Send one body; resolves with the answer, rejects on a refusal or no answer. */
  export type Transport<T extends Types> = (body: T["fields"]) => Promise<T["answer"]>;

  /** A write's answer (`Ledger.settle`): accepted, or refused with the error and the context its wrap leads with. */
  export type Verdict =
    | { readonly accepted: true }
    | { readonly accepted: false; readonly error: unknown; readonly context: string };

  /** The ACCEPTED verdict. */
  export const ACCEPTED: Verdict = Object.freeze({ accepted: true });

  /**
   * What `Ledger.settle` answers: the refusal's wrap (`null`: accepted), the
   * target right after, and the outcome of the take-backs the refusal sent
   * (`null` once every one landed or none was due; else the first failure —
   * the refusal itself when the owner's end abandoned it).
   */
  export type Settled<T extends Types> = {
    readonly error: Option<T["error"]>;
    readonly projection: Option<T["fields"]>;
    readonly confirmed: Option<T["fields"]>;
    readonly takenBack: Promise<Option<T["error"]>>;
  };

  /**
   * What `Ledger.forget` abandoned of a target: its confirmed base (`null`:
   * the ledger held no entry for it), how many writes in flight it
   * abandoned, and `landed` — resolves once each of them was answered, with
   * the bodies of those the server accepted after the edge, in issue order
   * (empty: none landed). A body there is what the server holds for its
   * fields as of its arrival, which the ledger no longer tracks: the owner
   * installs it and reads the target again.
   */
  export type Forgotten<T extends Types> = {
    readonly confirmed: Option<T["fields"]>;
    readonly abandoned: number;
    readonly landed: Promise<readonly T["fields"][]>;
  };

  /** One write in flight (compared by identity). */
  export type Write<T extends Types> = { readonly key: T["key"]; readonly fields: ReadonlySet<T["field"]> };

  /** One authoritative read in flight. */
  export type Read<T extends Types> = { readonly key: T["key"] };

  /** A multi-target write group (`Ledger.group`). */
  export interface Group<T extends Types> {
    add(write: Write<T>): void;
    members(): readonly Write<T>[];
    refuseAll(err: unknown, context: string): void;
  }
}

/** An abandoned write's answer (`Ledger.forget`): counted once; the last one resolves its `landed`. */
function lateAnswer<T extends Ledger.Types>(state: WriteState<T>, accepted: boolean): void {
  const gone = state.abandoned;
  if (gone == null) return;
  state.abandoned = null;
  if (accepted) gone.landed.push({ issued: state.issued, body: gone.bodyOf(state) });
  gone.pending--;
  if (gone.pending === 0) gone.done([...gone.landed].sort((a, b) => a.issued - b.issued).map((l) => l.body));
}

/** `gone`, which the caller checked is set. */
function nonnullOf<X>(gone: Option<X>): X {
  if (gone == null) throw new Error("Ledger: an abandonment that is not set");
  return gone;
}

/** The fields a sent body still unanswered carries. */
function openFields<T extends Ledger.Types>(entry: Entry<T>): Set<T["field"]> {
  const open = new Set<T["field"]>();
  for (const send of entry.sends) if (!send.answered) for (const f of send.fields) open.add(f);
  return open;
}
